import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash, Bell, GameController, Users, Trophy, Storefront, Star,
  MapPin, Target, Lightning, Crown
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_TYPES = [
  { value: 'open_seat', label: 'Open Seats', desc: 'When a spot opens in a game', icon: Users, color: '#39FF14' },
  { value: 'new_event', label: 'New Events', desc: 'When new events are posted', icon: Storefront, color: '#F97316' },
  { value: 'tournament', label: 'Tournaments', desc: 'New tournaments for a game', icon: Trophy, color: '#FFD700' },
  { value: 'favorite_game', label: 'Favorite Game Activity', desc: 'Any activity for your game', icon: Star, color: '#EF4444' },
  { value: 'beginner_event', label: 'Beginner Events', desc: 'New beginner-friendly events', icon: Target, color: '#10B981' },
  { value: 'shop_event', label: 'Shop Events', desc: 'Events from a specific shop', icon: Storefront, color: '#3B82F6' },
];

export default function SavedAlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [limit, setLimit] = useState({ limit: 3, used: 0, plan_code: 'player_free' });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ alert_type: 'open_seat', game_name: '', city: '', radius_miles: 25 });
  const [games, setGames] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API_URL}/api/notifications/saved-alerts`, { withCredentials: true }),
      axios.get(`${API_URL}/api/notifications/saved-alerts/limit`, { withCredentials: true }),
      axios.get(`${API_URL}/api/games`, { withCredentials: true }),
    ]).then(([a, l, g]) => {
      setAlerts(a.data || []);
      setLimit(l.data);
      setGames(g.data?.map(g2 => g2.name || g2.game_name) || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createAlert = async () => {
    if (!form.game_name && !form.city) {
      toast.error('Select a game or city');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/notifications/saved-alerts`, form, { withCredentials: true });
      toast.success('Alert saved!');
      setShowCreate(false);
      setForm({ alert_type: 'open_seat', game_name: '', city: '', radius_miles: 25 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create alert');
    }
  };

  const removeAlert = async (id) => {
    await axios.delete(`${API_URL}/api/notifications/saved-alerts/${id}`, { withCredentials: true }).catch(() => {});
    setAlerts(prev => prev.filter(a => a.id !== id));
    setLimit(prev => ({ ...prev, used: prev.used - 1 }));
    toast.success('Alert removed');
  };

  return (
    <div className="min-h-screen bg-[#0A0A12] pb-24" data-testid="saved-alerts-page">
      <div className="sticky top-0 z-20 bg-[#0A0A12]/95 backdrop-blur-lg border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-white font-bold text-lg" style={{ fontFamily: 'Unbounded' }}>Saved Alerts</h1>
            <p className="text-[10px] text-[#555]">{limit.used}/{limit.limit} alerts used</p>
          </div>
        </div>
        <button
          onClick={() => limit.used >= limit.limit ? toast.error(`Limit reached (${limit.limit}). Upgrade for more.`) : setShowCreate(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#39FF14]/10 text-[#39FF14] text-[11px] font-semibold hover:bg-[#39FF14]/20 transition-all"
          data-testid="create-alert-btn"
        >
          <Plus size={12} /> New Alert
        </button>
      </div>

      {/* Quota bar */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#0e0e16] border border-white/[0.04]">
          <div className="flex-1">
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(limit.used / limit.limit) * 100}%`, backgroundColor: limit.used >= limit.limit ? '#EF4444' : '#39FF14' }} />
            </div>
          </div>
          <span className="text-[10px] text-[#555]">{limit.used}/{limit.limit}</span>
          {limit.plan_code === 'player_free' && (
            <button onClick={() => navigate('/pricing')} className="flex items-center gap-1 text-[10px] text-[#F59E0B] font-medium">
              <Crown size={10} /> Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Alerts list */}
      <div className="px-4 py-3 space-y-2">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white/[0.02] rounded-xl animate-pulse" />)
        ) : alerts.length === 0 ? (
          <div className="text-center py-12">
            <Bell size={40} weight="duotone" className="text-[#333] mx-auto mb-3" />
            <p className="text-[#555] text-sm">No saved alerts</p>
            <p className="text-[#333] text-xs mt-1">Get notified when games you care about have activity</p>
          </div>
        ) : (
          alerts.map(a => {
            const type = ALERT_TYPES.find(t => t.value === a.alert_type) || ALERT_TYPES[0];
            const Icon = type.icon;
            return (
              <div key={a.id} className="bg-[#0e0e16] border border-white/[0.04] rounded-xl p-4 flex items-center gap-3" data-testid={`alert-${a.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${type.color}12` }}>
                  <Icon size={18} weight="duotone" style={{ color: type.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[13px] font-medium">{type.label}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {a.game_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF]">{a.game_name}</span>}
                    {a.city && <span className="text-[10px] text-[#555] flex items-center gap-0.5"><MapPin size={9} />{a.city}</span>}
                    {a.radius_miles && <span className="text-[10px] text-[#333]">{a.radius_miles}mi</span>}
                    {a.beginner_friendly_only && <span className="text-[10px] text-[#10B981]">Beginner</span>}
                  </div>
                </div>
                <button onClick={() => removeAlert(a.id)} className="p-2 rounded-lg hover:bg-[#EF4444]/10 text-[#444] hover:text-[#EF4444] transition-all">
                  <Trash size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-[480px] bg-[#0e0e16] border-t border-white/10 rounded-t-2xl p-5" onClick={e => e.stopPropagation()} data-testid="create-alert-modal">
            <h3 className="text-white font-bold text-base mb-4">New Saved Alert</h3>

            {/* Alert type */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {ALERT_TYPES.map(t => (
                <button key={t.value}
                  onClick={() => setForm(p => ({ ...p, alert_type: t.value }))}
                  className={`p-2.5 rounded-xl border text-center transition-all ${form.alert_type === t.value ? 'border-white/20 bg-white/5' : 'border-white/[0.04] hover:border-white/10'}`}
                  data-testid={`alert-type-${t.value}`}
                >
                  <t.icon size={18} weight="duotone" style={{ color: t.color }} className="mx-auto mb-1" />
                  <p className="text-[10px] text-white">{t.label}</p>
                </button>
              ))}
            </div>

            {/* Game select */}
            <select value={form.game_name} onChange={e => setForm(p => ({ ...p, game_name: e.target.value }))}
              className="w-full bg-[#181824] border border-white/[0.06] rounded-xl px-3 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-[#00F0FF]/30"
              data-testid="alert-game-select"
            >
              <option value="">Any game</option>
              {games.map(g => <option key={g} value={g}>{g}</option>)}
            </select>

            {/* City */}
            <input
              type="text" placeholder="City (optional)" value={form.city}
              onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
              className="w-full bg-[#181824] border border-white/[0.06] rounded-xl px-3 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-[#00F0FF]/30"
              data-testid="alert-city-input"
            />

            <button onClick={createAlert}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#39FF14]/20 to-[#00F0FF]/20 border border-[#39FF14]/20 text-white font-semibold text-sm hover:border-[#39FF14]/40 transition-all"
              data-testid="save-alert-btn"
            >
              Save Alert
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
